import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n, { translateErrorCode } from '../i18n';
import { recordarIdioma } from '../i18n/idioma';
import { useSession } from '../session/SessionContext';
import { urlDeAcceso } from '../session/acceso';

/** Por qué se llegó aquí desde una sesión abierta (`?salida=`), y qué decirle a la persona. */
const AVISOS_DE_SALIDA: Record<string, { titulo: string; texto: string }> = {
  inactividad: { titulo: 'login.exitIdleTitle', texto: 'login.exitIdleText' },
  'segundo-factor': { titulo: 'login.exitSecondFactorTitle', texto: 'login.exitSecondFactorText' },
  voluntaria: { titulo: 'login.exitManualTitle', texto: 'login.exitManualText' },
  // La misma persona entró en otro equipo: una sola sesión a la vez.
  'otro-dispositivo': { titulo: 'login.exitOtherDeviceTitle', texto: 'login.exitOtherDeviceText' },
};

/**
 * La puerta de entrada. No hay campo de contraseña aquí, ni lo habrá: el portal
 * no maneja credenciales, redirige al servidor de identidad (ADR-0002).
 *
 * Se aprovecha para decir tres cosas que el personal necesita saber antes de
 * entrar — qué es esto, que su contraseña no vive aquí, y que es de red interna.
 */
export function Portada() {
  const { t } = useTranslation();
  const { sesionNoLlego } = useSession();

  // El callback devuelve aquí con un motivo cuando el acceso falla. Mostrarlo es
  // la diferencia entre "algo pasó" y una persona mirando una pantalla en blanco.
  const motivo = new URLSearchParams(window.location.search).get('error');

  // La aplicación manda aquí a quien sacó por inactividad, o por administrar sin
  // código (ADR-0021). Se le dice qué pasó: una portada idéntica a la de siempre
  // haría pensar que algo falló.
  const [salida] = useState(() => new URLSearchParams(window.location.search).get('salida'));
  const avisoDeSalida = salida ? AVISOS_DE_SALIDA[salida] : undefined;
  useEffect(() => {
    if (salida) window.history.replaceState(null, '', window.location.pathname);
  }, [salida]);

  return (
    <div className="acceso">
      <section className="acceso__identidad">
        {/* Único sitio del portal donde el sello se ve entero y se lee: aquí hay
            espacio para los 150 px que necesita su anillo. */}
        <img className="acceso__escudo" src="/marca/escudo.png" alt={t('app.crestAlt')} />
        <span className="acceso__institucion">{t('app.institution')}</span>
        <h1 className="acceso__titulo">{t('app.name')}</h1>
        <p className="acceso__lema">{t('login.pitch')}</p>

        <ul className="acceso__puntos">
          <li>
            <span className="acceso__punto-titulo">{t('login.pointCurrentTitle')}</span>
            <span className="acceso__punto-texto">{t('login.pointCurrentText')}</span>
          </li>
          <li>
            <span className="acceso__punto-titulo">{t('login.pointGroupsTitle')}</span>
            <span className="acceso__punto-texto">{t('login.pointGroupsText')}</span>
          </li>
          <li>
            <span className="acceso__punto-titulo">{t('login.pointTraceTitle')}</span>
            <span className="acceso__punto-texto">{t('login.pointTraceText')}</span>
          </li>
        </ul>
      </section>

      <section className="acceso__tarjeta">
        {/* Español por omisión; quien prefiera inglés lo elige aquí, antes de
            entrar, y la pantalla de acceso de Keycloak sale ya en ese idioma. */}
        <div className="acceso__idioma" role="group" aria-label={t('login.languageLabel')}>
          {(['es-MX', 'en-US'] as const).map((idioma) => (
            <button
              key={idioma}
              type="button"
              className="acceso__idioma-opcion"
              aria-pressed={i18n.language === idioma}
              lang={idioma}
              onClick={() => {
                recordarIdioma(idioma);
                void i18n.changeLanguage(idioma);
              }}
            >
              {t(idioma === 'es-MX' ? 'login.languageEs' : 'login.languageEn')}
            </button>
          ))}
        </div>
        <span className="acceso__sello" aria-hidden="true">
          <svg viewBox="0 0 48 48" width="34" height="34" fill="none" stroke="currentColor" strokeWidth="1.4">
            <path d="M14 6h14l6 6v30a2 2 0 0 1-2 2H14a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2z" />
            <path d="M28 6v6h6M17 22h14M17 29h11M17 36h8" />
          </svg>
        </span>

        {motivo && (
          <p className="aviso aviso--error" role="alert" style={{ width: '100%' }}>
            {translateErrorCode(motivo)}
          </p>
        )}

        {avisoDeSalida && !motivo && (
          <div className="aviso aviso--info acceso__diagnostico" role="status">
            <strong>{t(avisoDeSalida.titulo)}</strong>
            <span>{t(avisoDeSalida.texto)}</span>
          </div>
        )}

        {/* Volver aquí tras un acceso correcto no es «no has entrado»: es que la
            sesión no llegó. Sin este aviso la persona repite el mismo botón
            indefinidamente, que es exactamente lo que ocurrió. */}
        {sesionNoLlego && !motivo && (
          <div className="aviso aviso--error acceso__diagnostico" role="alert">
            <strong>{t('login.sessionLostTitle')}</strong>
            <span>{t('login.sessionLostText')}</span>
            <span>{t('login.sessionLostHint')}</span>
          </div>
        )}

        <h2 className="acceso__tarjeta-titulo">{t('login.cardTitle')}</h2>
        <p className="acceso__tarjeta-texto">{t('login.explanation')}</p>

        <a className="boton boton--grande acceso__boton" href={urlDeAcceso()}>
          {t('login.action')}
        </a>

        {/* Quien no puede entrar no tiene a quién preguntarle a las 7 de la
            mañana. Este enlace le manda el correo para definir una contraseña
            nueva sin pasar por un administrador ni por otra plataforma. */}
        <a className="acceso__enlace" href="/api/v1/auth/password/forgot">
          {t('login.forgotPassword')}
        </a>

        <p className="acceso__nota">{t('login.internalOnly')}</p>
      </section>
    </div>
  );
}
